/**
 * Custom Triggers.
 *
 * This implementation previously shipped inside the free plugin's app.min.js
 * (Switch.js::listenCustomTriggers()), gated by custom_triggers_enabled being
 * forced to false/empty for free installs. WordPress.org guideline 5 does not
 * permit shipping locked feature code in the free plugin, so it lives here
 * instead and free's Switch.js no longer contains this logic at all.
 *
 * Copied verbatim from the free plugin's last working copy of
 * listenCustomTriggers() so behaviour is identical to before.
 *
 * Unlike the video/image extend* methods, there is no stub left in free to
 * monkey-patch onto - this is a standalone reimplementation, following
 * Accessibility.js's call-once/DOMContentLoaded-only pattern (not Premium.js's
 * call-twice pattern, since this only needs to run once DOM elements exist).
 */
class CustomTriggers {
	/**
	 * Init. Call exactly once, from DOMContentLoaded.
	 */
	init() {
		// Bail if the free plugin is not present.
		if (typeof wp_dark_mode_json === 'undefined' || !wp_dark_mode_json.options) {
			return;
		}

		// Bail if custom triggers not enabled.
		if (!wp_dark_mode_json.options.custom_triggers_enabled) {
			return;
		}

		// Get custom triggers from localized data.
		const triggers = wp_dark_mode_json.options.custom_triggers_triggers || [];

		// Bail if no triggers configured.
		if (!triggers || triggers.length === 0) {
			return;
		}

		const WPDarkMode = window.WPDarkMode;

		if (!WPDarkMode) {
			return;
		}

		// Use event delegation on window like free's listenSwitches() does.
		window?.addEventListener('click', (event) => {
			// Check each trigger selector.
			let matchedTrigger = null;

			for (const trigger of triggers) {
				// Skip if no selector.
				if (!trigger.element_selector || trigger.element_selector.trim() === '') {
					continue;
				}

				try {
					// Check if clicked element matches this trigger.
					const isCustomTrigger = event.target.closest(trigger.element_selector) || event.target.matches(trigger.element_selector);

					if (isCustomTrigger) {
						matchedTrigger = trigger;
						break;
					}
				} catch (error) {
					console.warn('WP Dark Mode: Invalid custom trigger selector:', trigger.element_selector, error);
				}
			}

			// Bail if no trigger matched.
			if (!matchedTrigger) {
				return;
			}

			// Prevent default action.
			event.preventDefault();

			// Execute action based on trigger setting.
			switch (matchedTrigger.action) {
				case 'toggle':
					WPDarkMode.toggle().remember();
					break;
				case 'switch_to_dark':
					if (!WPDarkMode.isActive) {
						WPDarkMode.activate().remember();
					}
					break;
				case 'switch_to_light':
					if (WPDarkMode.isActive) {
						WPDarkMode.deactivate().remember();
					}
					break;
			}
		}, true); // Use capture phase like listenSwitches does.

		// Add keyboard support (Enter and Space keys).
		window?.addEventListener('keydown', (event) => {
			if (event.key !== 'Enter' && event.key !== ' ') {
				return;
			}

			// Check each trigger selector.
			for (const trigger of triggers) {
				if (!trigger.element_selector || trigger.element_selector.trim() === '') {
					continue;
				}

				try {
					const isCustomTrigger = event.target.closest(trigger.element_selector) || event.target.matches(trigger.element_selector);

					if (isCustomTrigger) {
						event.preventDefault();
						event.target.click();
						break;
					}
				} catch (error) {
					// Ignore invalid selectors.
				}
			}
		}, true);
	}
}

export default new CustomTriggers();
